// React glue around the framework-agnostic KeyboardManager: owns one manager for
// the app, starts/stops it, and tracks the `?` shortcuts-modal open state.
//
// Authored IDENTICALLY across apps/web, apps/perps and marginx — keep in sync.

import { createContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { KeyboardManager } from "./KeyboardManager";
import { SHORTCUTS } from "./shortcuts";
import type { ShortcutHandler } from "./types";

export interface KeyboardContextValue {
  manager: KeyboardManager;
  shortcutsOpen: boolean;
  setShortcutsOpen: (open: boolean) => void;
}

export const KeyboardContext = createContext<KeyboardContextValue | null>(null);

export function KeyboardProvider({ children }: { children: ReactNode }) {
  const manager = useMemo(() => new KeyboardManager(SHORTCUTS), []);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const openRef = useRef(shortcutsOpen);
  openRef.current = shortcutsOpen;

  useEffect(() => {
    manager.start();
    return () => manager.stop();
  }, [manager]);

  // Built-in global handlers: `?` toggles the shortcuts modal, `Esc` closes it.
  useEffect(() => {
    const onShortcuts: ShortcutHandler = () => setShortcutsOpen((o) => !o);
    const onCancel: ShortcutHandler = () => {
      if (openRef.current) {
        setShortcutsOpen(false);
        return; // consumed
      }
      return false; // let other Esc handlers (close dialogs, blur) run
    };
    manager.addHandler("global.shortcuts", onShortcuts);
    manager.addHandler("global.cancel", onCancel);
    return () => {
      manager.removeHandler("global.shortcuts", onShortcuts);
      manager.removeHandler("global.cancel", onCancel);
    };
  }, [manager]);

  const value = useMemo<KeyboardContextValue>(
    () => ({ manager, shortcutsOpen, setShortcutsOpen }),
    [manager, shortcutsOpen],
  );

  return <KeyboardContext.Provider value={value}>{children}</KeyboardContext.Provider>;
}
